import { Card, CardContent } from "@/components/ui/card";
import { Calendar, XCircle, ShoppingBag, Trophy, Loader2, AlertTriangle, CreditCard, Clock, Percent } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utils/formatPrice";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import type { CustomerDetailStats } from "@/hooks/useCustomerDetailData";

interface CustomerQuickStatsProps {
  stats: CustomerDetailStats | null;
  isLoading: boolean;
}

export default function CustomerQuickStats({ stats, isLoading }: CustomerQuickStatsProps) {
  const { t } = useTranslation("admin");
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const items = [
    { icon: Calendar, value: stats?.totalBookings ?? 0, label: t("customer.stats.bookings") },
    { icon: XCircle, value: stats?.cancellations ?? 0, label: t("customer.stats.cancellations") },
    { icon: ShoppingBag, value: formatPrice(stats?.totalSpent ?? 0), label: t("customer.stats.ltv") },
    { icon: Trophy, value: stats?.teamsCount ?? 0, label: t("customer.stats.teams") },
    { icon: AlertTriangle, value: stats?.noShows ?? 0, label: t("customer.stats.noShows") },
    { icon: CreditCard, value: formatPrice(stats?.unpaidAmount ?? 0), label: t("customer.stats.unpaid"), destructive: (stats?.unpaidAmount ?? 0) > 0 },
    { icon: Percent, value: `${stats?.cancelRate ?? 0}%`, label: t("customer.stats.cancelRate") },
    { icon: Clock, value: stats?.lastActive ? formatDistanceToNow(new Date(stats.lastActive), { addSuffix: true, locale: sv }) : "–", label: t("customer.stats.lastActive") },
  ];

  return (
    <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
      {items.map((item) => (
        <Card key={item.label}>
          <CardContent className="p-2.5 text-center">
            <item.icon className="h-3.5 w-3.5 mx-auto text-muted-foreground mb-0.5" />
            <p className={`text-base font-bold truncate ${"destructive" in item && item.destructive ? "text-destructive" : ""}`}>{item.value}</p>
            <p className="text-[10px] text-muted-foreground">{item.label}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
